import type { LegislationFile, LegislativeSourceSuggestion, LexData, PracticeArea } from "./types";

export type WatchJurisdictionId = "EU" | "LU" | "FR" | "BE" | "CH";

export type LegislativeWatchSource = {
  id: WatchJurisdictionId;
  label: string;
  jurisdiction: string;
  primarySources: Array<{ label: string; href: string; kind: "official" | "open data" | "parliament" | "third-party" }>;
  automation: string;
  coverage: string[];
  recommendation: string;
  risk: string;
};

export type CuratedLegislationFile = LegislationFile & {
  isCuratedWatch: true;
  watchJurisdictionId: WatchJurisdictionId;
  sourceType: "official API" | "SPARQL" | "official portal" | "third-party scraper";
  automationStatus: "ready to connect" | "needs credentials" | "scraper fallback";
  coverageTags: string[];
  sourceLinks: Array<{ label: string; href: string }>;
  ingestionNotes: string;
};

export type LegislativeNewsItem = {
  id: string;
  title: string;
  sourceLabel: string;
  jurisdiction: string;
  publishedLabel: string;
  sourceUrl: string;
  imageUrl?: string | null;
  imageAlt: string;
  excerpt: string;
  whyItMatters: string;
  topics: string[];
  status: "New" | "Updated" | "Consultation" | "Implementation";
  linkedFileId: string;
  updatedAt: number;
};

export type SourceValidationResult = {
  accepted: boolean;
  normalizedUrl: string;
  label: string;
  summary: string;
  matchedTerms: string[];
  practiceAreaIds: string[];
};

export const legislativeWatchTopics = ["AI", "IP", "IT", "Fintech", "3D printing", "Data", "Digital services", "Cybersecurity"];

export const legislativeSourceKeywordGroups = {
  legal: ["law", "legal", "legislation", "regulation", "regulatory", "directive", "decree", "act", "bill", "consultation", "parliament", "gazette", "official journal", "compliance", "juridique", "loi", "règlement", "decret", "projet de loi", "consultation", "recht", "gesetz"],
  "AI": ["ai", "artificial intelligence", "intelligence artificielle", "algorithmic", "machine learning", "automated decision"],
  "Data": ["data", "personal data", "privacy", "gdpr", "données", "protection des données", "open data", "data act"],
  "Digital services": ["digital services", "platform", "online platform", "eidas", "electronic signature", "communications électroniques", "digitalisation", "numérique"],
  "Fintech": ["fintech", "financial", "payments", "crypto", "stablecoin", "dora", "banking", "finance"],
  "IP": ["intellectual property", "copyright", "trademark", "patent", "propriété intellectuelle", "droit d'auteur"],
  "Cybersecurity": ["cybersecurity", "cyber", "security", "incident", "cloud", "informatique en nuage", "network security"],
  "3D printing": ["3d printing", "additive manufacturing", "impression 3d"],
} as const;

export const legislativeWatchSources: LegislativeWatchSource[] = [
  {
    id: "EU",
    label: "European Union",
    jurisdiction: "EU institutions",
    primarySources: [
      { label: "EUR-Lex / Cellar", href: "https://eur-lex.europa.eu/", kind: "official" },
      { label: "Commission tracking law-making", href: "https://commission.europa.eu/law/track-law-making_en", kind: "official" },
      { label: "EP Legislative Observatory", href: "https://oeil.secure.europarl.europa.eu/oeil/en/", kind: "parliament" },
      { label: "EP Open Data API", href: "https://data.europarl.europa.eu/en/developer-corner/opendata-api", kind: "open data" },
    ],
    automation: "Poll EUR-Lex/Cellar and EP open data; use Commission initiatives for early-stage calls and consultations.",
    coverage: ["AI", "IP", "IT", "Fintech", "3D printing", "Data"],
    recommendation: "Make this the first automated connector because it anchors EU measures and national implementation tracking.",
    risk: "Procedure status and consolidated text timing can diverge, so keep links back to authoritative records.",
  },
  {
    id: "LU",
    label: "Luxembourg",
    jurisdiction: "Luxembourg",
    primarySources: [
      { label: "Legilux", href: "https://legilux.public.lu/", kind: "official" },
      { label: "Legilux SPARQL", href: "https://data.legilux.public.lu/sparqlendpoint", kind: "open data" },
      { label: "data.public.lu", href: "https://data.public.lu/", kind: "open data" },
    ],
    automation: "Poll Legilux linked data for new or modified ELI records, then enrich with sector keywords.",
    coverage: ["AI", "IT", "Fintech", "Data"],
    recommendation: "Use Legilux as the canonical national connector and map ELI URIs into the common LexMosaic schema.",
    risk: "Subject metadata may need keyword enrichment for niche topics such as 3D printing.",
  },
  {
    id: "FR",
    label: "France",
    jurisdiction: "France",
    primarySources: [
      { label: "Legifrance API", href: "https://www.legifrance.gouv.fr/contenu/pied-de-page/foire-aux-questions-api", kind: "official" },
      { label: "PISTE", href: "https://piste.gouv.fr/", kind: "open data" },
      { label: "Tricoteuses", href: "https://tricoteuses.fr/en/", kind: "third-party" },
    ],
    automation: "Use PISTE credentials for Legifrance; supplement parliamentary movement through Tricoteuses.",
    coverage: ["AI", "IP", "IT", "Fintech", "Data"],
    recommendation: "Use the official API for legal texts and Tricoteuses for parliamentary documents and changes.",
    risk: "PISTE onboarding and API quotas need owner credentials before production polling.",
  },
  {
    id: "BE",
    label: "Belgium",
    jurisdiction: "Belgium",
    primarySources: [
      { label: "Moniteur belge / Justel", href: "https://n-lex.europa.eu/n-lex/info/info-be/index", kind: "official" },
      { label: "Belgian Gazette scraper option", href: "https://apify.com/studio-amba/staatsblad-scraper/api", kind: "third-party" },
    ],
    automation: "Start with Moniteur/Justel monitoring; use a scraper only as a pragmatic bridge if no official API is available.",
    coverage: ["AI", "IP", "IT", "Fintech", "Data"],
    recommendation: "Treat Belgian data as a curated monitored source until a stable official machine endpoint is selected.",
    risk: "Third-party scraping should be marked as non-authoritative and reconciled against the official gazette link.",
  },
  {
    id: "CH",
    label: "Switzerland / Romandie",
    jurisdiction: "Switzerland",
    primarySources: [
      { label: "Fedlex", href: "https://www.fedlex.admin.ch/", kind: "official" },
      { label: "Fedlex SPARQL", href: "https://fedlex.data.admin.ch/sparqlendpoint", kind: "open data" },
      { label: "LINDAS Fedlex documentation", href: "https://ld.admin.ch/data-usage/fedlex/", kind: "open data" },
    ],
    automation: "Poll Fedlex SPARQL for federal legislation, official compilations, gazette items and consultations.",
    coverage: ["AI", "IP", "IT", "Fintech", "Data"],
    recommendation: "Connect federal Fedlex first; add canton-level Romandie sources later as separate source profiles.",
    risk: "Cantonal Romandie sources are heterogeneous and should not be mixed into the federal connector.",
  },
];

export function createCuratedLegislationFiles(data: LexData, now = Date.now()): CuratedLegislationFile[] {
  const digitalPracticeAreaIds = findPracticeAreaIds(data.practiceAreas, ["data", "privacy", "regulatory", "technology", "digital", "intellectual", "financial", "cyber"]);
  const fallbackPracticeAreaIds = digitalPracticeAreaIds.length > 0 ? digitalPracticeAreaIds : data.practiceAreas.slice(0, 2).map((area) => area.id);
  const ownerFirmId = data.viewerFirm?.id ?? data.firms[0]?.id ?? null;
  const createdByUserId = data.viewer.id;

  return [
    watchFile({
      id: "watch-eu-digital-ai",
      title: "EU digital, AI, IP and fintech legislative watch",
      jurisdictionOrInstitution: "EU institutions",
      initiativeName: "EUR-Lex, Commission and EP watch",
      sourceUrl: "https://commission.europa.eu/law/track-law-making_en",
      summary: "Curated watch source for EU regulations, directives, Commission initiatives and Parliament procedures touching AI, IP, IT, fintech, data and 3D printing.",
      sourceType: "SPARQL",
      automationStatus: "ready to connect",
      watchJurisdictionId: "EU",
      coverageTags: ["AI", "IP", "IT", "Fintech", "3D printing", "Data"],
      sourceLinks: [
        { label: "EUR-Lex", href: "https://eur-lex.europa.eu/" },
        { label: "Commission tracking", href: "https://commission.europa.eu/law/track-law-making_en" },
        { label: "EP Observatory", href: "https://oeil.secure.europarl.europa.eu/oeil/en/" },
      ],
      ingestionNotes: "Primary connector should poll EUR-Lex/Cellar and EP open data, then use Commission initiatives for early consultation alerts.",
    }, fallbackPracticeAreaIds, ownerFirmId, createdByUserId, now - 2 * day, now),
    watchFile({
      id: "watch-lu-legilux",
      title: "Luxembourg Legilux digital regulation watch",
      jurisdictionOrInstitution: "Luxembourg",
      initiativeName: "Legilux linked data watch",
      sourceUrl: "https://data.legilux.public.lu/sparqlendpoint",
      summary: "Tracks Luxembourg laws and regulations through Legilux ELI/linked-data records, with emphasis on AI, fintech, IT, data and national EU implementation measures.",
      sourceType: "SPARQL",
      automationStatus: "ready to connect",
      watchJurisdictionId: "LU",
      coverageTags: ["AI", "IT", "Fintech", "Data"],
      sourceLinks: [
        { label: "Legilux", href: "https://legilux.public.lu/" },
        { label: "Legilux SPARQL", href: "https://data.legilux.public.lu/sparqlendpoint" },
      ],
      ingestionNotes: "Use ELI URI and publication metadata as stable identifiers; enrich sparse topic metadata with keyword filters.",
    }, fallbackPracticeAreaIds, ownerFirmId, createdByUserId, now - 36 * hour, now),
    watchFile({
      id: "watch-fr-legifrance",
      title: "France Legifrance and Parliament digital/IP watch",
      jurisdictionOrInstitution: "France",
      initiativeName: "Legifrance API and Tricoteuses watch",
      sourceUrl: "https://www.legifrance.gouv.fr/contenu/pied-de-page/foire-aux-questions-api",
      summary: "Monitors French laws, decrees, JORF publications and parliamentary movement relevant to AI, intellectual property, digital services, fintech and data.",
      sourceType: "official API",
      automationStatus: "needs credentials",
      watchJurisdictionId: "FR",
      coverageTags: ["AI", "IP", "IT", "Fintech", "Data"],
      sourceLinks: [
        { label: "Legifrance API FAQ", href: "https://www.legifrance.gouv.fr/contenu/pied-de-page/foire-aux-questions-api" },
        { label: "PISTE", href: "https://piste.gouv.fr/" },
        { label: "Tricoteuses", href: "https://tricoteuses.fr/en/" },
      ],
      ingestionNotes: "Official legal text access should use PISTE/Legifrance credentials; Tricoteuses can supplement parliamentary changes.",
    }, fallbackPracticeAreaIds, ownerFirmId, createdByUserId, now - 30 * hour, now),
    watchFile({
      id: "watch-be-moniteur",
      title: "Belgium Moniteur and Justel digital law watch",
      jurisdictionOrInstitution: "Belgium",
      initiativeName: "Moniteur belge and Justel watch",
      sourceUrl: "https://n-lex.europa.eu/n-lex/info/info-be/index",
      summary: "Curated Belgian watch for official gazette and consolidated-law movements in AI, IP, IT, fintech and data topics.",
      sourceType: "official portal",
      automationStatus: "scraper fallback",
      watchJurisdictionId: "BE",
      coverageTags: ["AI", "IP", "IT", "Fintech", "Data"],
      sourceLinks: [
        { label: "N-Lex Belgium", href: "https://n-lex.europa.eu/n-lex/info/info-be/index" },
        { label: "Scraper option", href: "https://apify.com/studio-amba/staatsblad-scraper/api" },
      ],
      ingestionNotes: "Keep official Moniteur/Justel links as the authority; any scraper output should be reconciled against official gazette records.",
    }, fallbackPracticeAreaIds, ownerFirmId, createdByUserId, now - 24 * hour, now),
    watchFile({
      id: "watch-ch-fedlex",
      title: "Switzerland and Romandie Fedlex watch",
      jurisdictionOrInstitution: "Switzerland",
      initiativeName: "Fedlex federal and consultation watch",
      sourceUrl: "https://fedlex.data.admin.ch/sparqlendpoint",
      summary: "Tracks Swiss federal legislation, official compilations, gazette items and consultations relevant to AI, IP, IT, fintech and data, with Romandie expansion planned at cantonal level.",
      sourceType: "SPARQL",
      automationStatus: "ready to connect",
      watchJurisdictionId: "CH",
      coverageTags: ["AI", "IP", "IT", "Fintech", "Data"],
      sourceLinks: [
        { label: "Fedlex", href: "https://www.fedlex.admin.ch/" },
        { label: "Fedlex SPARQL", href: "https://fedlex.data.admin.ch/sparqlendpoint" },
        { label: "LINDAS documentation", href: "https://ld.admin.ch/data-usage/fedlex/" },
      ],
      ingestionNotes: "Use federal Fedlex linked data first; add canton-specific Romandie feeds as separate connectors after source selection.",
    }, fallbackPracticeAreaIds, ownerFirmId, createdByUserId, now - 18 * hour, now),
  ];
}

export function createLegislativeNewsItems(now = Date.now()): LegislativeNewsItem[] {
  return [
    newsItem({
      id: "news-eu-ai-omnibus",
      title: "EU reaches political agreement on simpler AI Act rules",
      sourceLabel: "European Commission",
      jurisdiction: "EU institutions",
      publishedLabel: "7 May 2026",
      sourceUrl: "https://digital-strategy.ec.europa.eu/en/news/eu-agrees-simplify-ai-rules-boost-innovation-and-ban-nudification-apps-protect-citizens",
      imageUrl: "https://digital-strategy.ec.europa.eu/sites/default/files/styles/newsroom_large/public/newsroom/items/1GPAIcode_of_practice_UpTTE8saBtT4lu7YltGuGWUJYM_232100.jpg?itok=l9gJDFJE",
      imageAlt: "Illustration from the European Commission article showing AI, legal and digital elements.",
      excerpt: "The Commission says the agreement adjusts the implementation timeline for high-risk AI systems and keeps the AI Act focused on innovation, safety and fundamental rights.",
      whyItMatters: "Members advising AI deployers should track the new timing for high-risk systems and the expected support tools before full compliance planning is locked.",
      topics: ["AI", "Digital services", "Data"],
      status: "New",
      linkedFileId: "watch-eu-digital-ai",
      offsetDays: 0,
    }, now),
    newsItem({
      id: "news-eu-data-act",
      title: "EU Data Act application becomes a live compliance topic",
      sourceLabel: "European Commission",
      jurisdiction: "EU institutions",
      publishedLabel: "Updated 2026",
      sourceUrl: "https://digital-strategy.ec.europa.eu/en/policies/data-act",
      imageUrl: "https://digital-strategy.ec.europa.eu/sites/default/files/styles/newsroom_thumbnail/public/2025-12/GettyImages-1420699461%20%C2%A9%20Userba011d64_201.jpg?itok=1m-B5dve",
      imageAlt: "European Commission image showing data organisation over a laptop.",
      excerpt: "The Data Act sets harmonised rules on fair access to and use of data, with practical implications for connected products, cloud switching and business-to-government data access.",
      whyItMatters: "This is a cross-practice trigger for technology, contracting, competition, privacy and regulated-industry teams.",
      topics: ["Data", "IT", "Fintech"],
      status: "Implementation",
      linkedFileId: "watch-eu-digital-ai",
      offsetDays: 1,
    }, now),
    newsItem({
      id: "news-lu-ai-act-action",
      title: "Luxembourg maps AI Act obligations into national governance",
      sourceLabel: "Gouvernement luxembourgeois",
      jurisdiction: "Luxembourg",
      publishedLabel: "20 January 2026",
      sourceUrl: "https://gouvernement.lu/fr/actualites/toutes_actualites/communiques/2026/01-janvier/20-margue-ai-act.html",
      imageUrl: "https://gouvernement.lu/fr/_jcr_content/image.damimg.jpeg/1768931262304/content/dam/gouvernement2024/images-documents/actualites/2026/01-janvier/20-margue-ai-act/134134014704246722.jpg",
      imageAlt: "Luxembourg AI Act in Action conference image.",
      excerpt: "The AI Act in Action conference focused on practical compliance, national governance and the role of regulators mentioned in draft bill no. 8476.",
      whyItMatters: "Luxembourg members can infer which authorities and client obligations are likely to matter as AI Act implementation becomes operational.",
      topics: ["AI", "Data"],
      status: "Implementation",
      linkedFileId: "watch-lu-legilux",
      offsetDays: 2,
    }, now),
    newsItem({
      id: "news-lu-data-strategy",
      title: "Luxembourg Data Strategy frames data, AI and quantum as sovereignty priorities",
      sourceLabel: "Gouvernement luxembourgeois",
      jurisdiction: "Luxembourg",
      publishedLabel: "Updated 6 February 2026",
      sourceUrl: "https://gouvernement.lu/en/dossiers.gouv2024_mindigital%2Ben%2Bdossiers%2B2025%2Bstrategie-luxembourg-donnees.html",
      imageUrl: "https://mindigital.gouvernement.lu/en/dossiers/2025/strategie-luxembourg-donnees/_jcr_content/root/root-responsivegrid/content-responsivegrid/sections-responsivegrid/section_745366095/col2/image.coreimg.jpeg/1764576791037/data-en.jpeg",
      imageAlt: "Luxembourg Data Strategy visual from the government dossier.",
      excerpt: "Luxembourg presents data as a raw material for digital innovation and links data, AI and quantum technologies to the country's digital sovereignty agenda.",
      whyItMatters: "Useful for spotting future public-sector, data-governance and innovation-law work before formal legislative texts arrive.",
      topics: ["Data", "AI", "IT"],
      status: "Updated",
      linkedFileId: "watch-lu-legilux",
      offsetDays: 3,
    }, now),
    newsItem({
      id: "news-fr-simplification-ai",
      title: "France publishes economic simplification law with AI and data-protection language",
      sourceLabel: "Legifrance",
      jurisdiction: "France",
      publishedLabel: "27 May 2026",
      sourceUrl: "https://www.legifrance.gouv.fr/eli/loi/2026/5/26/2026-403/jo/texte",
      imageUrl: null,
      imageAlt: "Official Legifrance source branding.",
      excerpt: "Law no. 2026-403 includes language on promoting artificial-intelligence innovation while respecting privacy and personal-data protection.",
      whyItMatters: "This gives French digital and privacy teams a concrete enacted text to review, not only a policy signal.",
      topics: ["AI", "Data", "IT"],
      status: "New",
      linkedFileId: "watch-fr-legifrance",
      offsetDays: 0,
    }, now),
    newsItem({
      id: "news-fr-cloud-data",
      title: "France adopts decree on sensitive public-sector data in private cloud services",
      sourceLabel: "Legifrance",
      jurisdiction: "France",
      publishedLabel: "16 April 2026",
      sourceUrl: "https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000053900789",
      imageUrl: null,
      imageAlt: "Official Legifrance source branding.",
      excerpt: "The decree concerns protection of particularly sensitive data processed by state administrations, operators and public-interest groups through private cloud services.",
      whyItMatters: "Cloud, outsourcing, cybersecurity and public-procurement mandates can use this as a concrete regulatory checkpoint.",
      topics: ["Data", "IT", "Cybersecurity"],
      status: "Implementation",
      linkedFileId: "watch-fr-legifrance",
      offsetDays: 4,
    }, now),
    newsItem({
      id: "news-be-electronic-signature",
      title: "Belgium approves remote qualified electronic signature service at second reading",
      sourceLabel: "News.belgium",
      jurisdiction: "Belgium",
      publishedLabel: "13 May 2026",
      sourceUrl: "https://news.belgium.be/fr/instauration-dun-service-de-signatures-electroniques-qualifiees-deuxieme-lecture",
      imageUrl: "https://news.belgium.be/sites/default/files/styles/source_teaser/public/sources/logo/Watz.jpg?itok=OGUjXnBX",
      imageAlt: "Vanessa Matz portrait from the News.belgium article.",
      excerpt: "The Belgian Council of Ministers approved a draft law for a new remote qualified electronic-signature service based on remotely issued certificates.",
      whyItMatters: "This is directly relevant to eIDAS, identity, public services, professional signatures and cross-border digital transactions.",
      topics: ["Digital services", "IT", "Data"],
      status: "New",
      linkedFileId: "watch-be-moniteur",
      offsetDays: 1,
    }, now),
    newsItem({
      id: "news-be-communications",
      title: "Belgium advances draft provisions on electronic communications",
      sourceLabel: "News.belgium",
      jurisdiction: "Belgium",
      publishedLabel: "29 May 2026",
      sourceUrl: "https://news.belgium.be/fr/dispositions-diverses-en-matiere-de-communications-electroniques-2",
      imageUrl: "https://news.belgium.be/sites/default/files/styles/source_teaser/public/sources/logo/Watz.jpg?itok=OGUjXnBX",
      imageAlt: "Vanessa Matz portrait from the News.belgium article.",
      excerpt: "The Council of Ministers approved draft provisions amending electronic-communications rules, including telecom complaint handling and regulator-related legislation.",
      whyItMatters: "Members can spot telecom, platform, evidence and regulatory-service implications early in the Belgian legislative path.",
      topics: ["Digital services", "IT"],
      status: "New",
      linkedFileId: "watch-be-moniteur",
      offsetDays: 0,
    }, now),
    newsItem({
      id: "news-be-data-act",
      title: "Belgium prepares national implementation of the EU Data Act",
      sourceLabel: "News.belgium",
      jurisdiction: "Belgium",
      publishedLabel: "27 March 2026",
      sourceUrl: "https://news.belgium.be/fr/mise-en-oeuvre-du-reglement-europeen-sur-les-donnees",
      imageUrl: "https://news.belgium.be/sites/default/files/styles/source_teaser/public/sources/logo/Clarinval.jpg?itok=RLSp9zea",
      imageAlt: "David Clarinval portrait from the News.belgium article.",
      excerpt: "A Belgian draft law implements Regulation (EU) 2023/2854 on harmonised rules for fair access to and use of data.",
      whyItMatters: "This lets members compare Belgian implementation choices with EU-level Data Act obligations.",
      topics: ["Data", "Digital services"],
      status: "Implementation",
      linkedFileId: "watch-be-moniteur",
      offsetDays: 5,
    }, now),
    newsItem({
      id: "news-ch-ai-regulation",
      title: "Switzerland prepares AI consultation draft for the end of 2026",
      sourceLabel: "Federal Chancellery",
      jurisdiction: "Switzerland",
      publishedLabel: "Updated 2026",
      sourceUrl: "https://www.bk.admin.ch/bk/en/home/digitale-transformation-ikt-lenkung/kuenstliche_intelligenz.html",
      imageUrl: "https://www.bk.admin.ch/images/swiss-logo-name.svg",
      imageAlt: "Swiss Federal Chancellery source branding.",
      excerpt: "The Federal Council is preparing a consultation draft on AI regulation, focused on implementing the Council of Europe AI Convention and targeted legal measures.",
      whyItMatters: "Romandie and Swiss desks should track this now because the Swiss path is expected to differ from the EU AI Act model.",
      topics: ["AI", "Data", "Digital services"],
      status: "Consultation",
      linkedFileId: "watch-ch-fedlex",
      offsetDays: 2,
    }, now),
    newsItem({
      id: "news-ch-platforms",
      title: "Swiss platform and search-engine law consultation signals digital-services regulation",
      sourceLabel: "Swiss Federal Council",
      jurisdiction: "Switzerland",
      publishedLabel: "Consultation closed 16 February 2026",
      sourceUrl: "https://www.efd.admin.ch/en/newnsb/6TmEAde4htulaWG9CWYtK",
      imageUrl: "https://www.efd.admin.ch/images/swiss-logo-name.svg",
      imageAlt: "Swiss Federal Department of Finance source branding.",
      excerpt: "The Federal Council consulted on a law to strengthen user rights and transparency for very large communication platforms and search engines.",
      whyItMatters: "This is a practical watch item for platform governance, online-content rules and Swiss-EU digital divergence.",
      topics: ["Digital services", "IT", "Data"],
      status: "Consultation",
      linkedFileId: "watch-ch-fedlex",
      offsetDays: 6,
    }, now),
  ].sort((a, b) => b.updatedAt - a.updatedAt);
}

export function createPersonalizedLegislativeNewsItems(data: LexData, now = Date.now()) {
  const items = [...createNetworkLegislativeNewsItems(data), ...createLegislativeNewsItems(now)].sort((a, b) => b.updatedAt - a.updatedAt);
  const viewerPracticeAreaIds = new Set(data.userPracticeAreas.filter((link) => link.userId === data.viewer.id).map((link) => link.practiceAreaId));
  const viewerPracticeAreas = data.practiceAreas.filter((area) => viewerPracticeAreaIds.has(area.id));
  if (viewerPracticeAreas.length === 0) return items;
  const viewerTerms = new Set(viewerPracticeAreas.flatMap((area) => termsForPracticeArea(area)));
  return items.filter((item) => item.topics.some((topic) => viewerTerms.has(topic.toLowerCase())) || item.topics.some((topic) => viewerPracticeAreas.some((area) => `${area.name} ${area.description}`.toLowerCase().includes(topic.toLowerCase()))));
}

export function createNetworkLegislativeNewsItems(data: LexData): LegislativeNewsItem[] {
  return (data.legislativeFeedItems ?? [])
    .filter((item) => (item.visibilityStatus ?? "visible") === "visible" && (item.reviewStatus ?? "approved") === "approved")
    .map((item) => ({
    id: `pulled-${item.id}`,
    title: item.title,
    sourceLabel: item.sourceLabel,
    jurisdiction: item.jurisdiction ?? "Network source",
    publishedLabel: item.publishedLabel,
    sourceUrl: item.sourceUrl,
    imageUrl: item.imageUrl ?? null,
    imageAlt: `${item.sourceLabel} source image`,
    excerpt: item.excerpt,
    whyItMatters: item.externalId
      ? `This reviewed item carries a stable ${item.externalIdKind ?? "external"} reference (${item.externalId}) and matched your LexMosaic practice areas.`
      : "This reviewed item was pulled from a member-approved source and matched your LexMosaic practice areas.",
    topics: item.topics,
    status: item.status,
    linkedFileId: linkedWatchFileIdForJurisdiction(item.jurisdiction),
    updatedAt: item.updatedAt,
  }));
}

export function acceptedLegislativeSources(data: LexData) {
  const acceptedSuggestions = (data.legislativeSources ?? []).filter((source) => source.status === "accepted");
  const definitions = (data.legislativeSourceDefinitions ?? []).map((definition) => ({
    id: `definition-${definition.id}`,
    sourceDefinitionId: definition.id,
    url: definition.discoveryUrl,
    normalizedUrl: definition.normalizedUrl,
    label: definition.label,
    jurisdiction: definition.jurisdiction,
    status: definition.active ? "accepted" as const : "pending" as const,
    validationStatus: definition.lastStatus === "error" ? "rejected" as const : "accepted" as const,
    validationSummary: definition.lastStatusMessage ?? `${definition.sourceType.replace(/_/g, " ")} source using ${definition.parserKind.replace(/_/g, " ")} extraction.`,
    matchedTerms: definition.topicTags,
    practiceAreaIds: definition.practiceAreaIds,
    suggestedByUserId: definition.suggestedByUserId ?? definition.createdByUserId ?? "system",
    lastCheckedAt: definition.lastPollAt ?? null,
    lastPullAt: definition.lastPollAt ?? null,
    lastSuccessfulParseAt: definition.lastSuccessfulParseAt ?? null,
    lastStatus: definition.lastStatus,
    lastStatusMessage: definition.lastStatusMessage,
    lastModified: definition.lastModified,
    etag: definition.etag,
    sourceType: definition.sourceType,
    parserKind: definition.parserKind,
    createdAt: definition.createdAt,
    updatedAt: definition.updatedAt,
  }));
  return [...definitions, ...acceptedSuggestions];
}

export function validateSuggestedSourceInput(input: { url: string; label?: string; contentText?: string; practiceAreas: PracticeArea[] }): SourceValidationResult {
  const normalizedUrl = normalizeSourceUrl(input.url);
  const label = input.label?.trim() || sourceLabelFromUrl(normalizedUrl);
  const contentText = `${input.contentText ?? ""} ${normalizedUrl} ${label}`.toLowerCase();
  const legalMatches = matchTerms(contentText, legislativeSourceKeywordGroups.legal);
  const topicMatches = Object.entries(legislativeSourceKeywordGroups)
    .filter(([group]) => group !== "legal")
    .flatMap(([group, terms]) => matchTerms(contentText, terms).map((term) => ({ group, term })));
  const matchedPracticeAreaIds = input.practiceAreas
    .filter((area) => {
      const areaTerms = termsForPracticeArea(area);
      return topicMatches.some((match) => areaTerms.includes(match.group.toLowerCase()) || areaTerms.some((term) => match.term.includes(term) || term.includes(match.term)));
    })
    .map((area) => area.id);

  if (!/^https?:\/\//i.test(normalizedUrl)) {
    return { accepted: false, normalizedUrl, label, summary: "Use a valid http or https source URL.", matchedTerms: [], practiceAreaIds: [] };
  }
  if (legalMatches.length === 0) {
    return { accepted: false, normalizedUrl, label, summary: "Rejected: the source does not appear to contain legal, regulatory or legislative content.", matchedTerms: [], practiceAreaIds: [] };
  }
  if (topicMatches.length === 0 || matchedPracticeAreaIds.length === 0) {
    return { accepted: false, normalizedUrl, label, summary: "Rejected: the source did not match LexMosaic expertise areas such as AI, data, digital services, fintech, IP or cybersecurity.", matchedTerms: [...new Set([...legalMatches, ...topicMatches.map((match) => match.term)])], practiceAreaIds: [] };
  }
  return {
    accepted: true,
    normalizedUrl,
    label,
    summary: `Accepted: matched legal content and ${matchedPracticeAreaIds.length} LexMosaic practice area${matchedPracticeAreaIds.length === 1 ? "" : "s"}.`,
    matchedTerms: [...new Set([...legalMatches, ...topicMatches.map((match) => match.term)])],
    practiceAreaIds: matchedPracticeAreaIds,
  };
}

export function localSuggestedSource(input: { url: string; label?: string; validation: SourceValidationResult; userId: string; now?: number }): LegislativeSourceSuggestion {
  const now = input.now ?? Date.now();
  return {
    id: `local-source-${now}`,
    url: input.url,
    normalizedUrl: input.validation.normalizedUrl,
    label: input.validation.label || input.label || sourceLabelFromUrl(input.url),
    jurisdiction: null,
    status: input.validation.accepted ? "accepted" : "rejected",
    validationStatus: input.validation.accepted ? "accepted" : "rejected",
    validationSummary: input.validation.summary,
    matchedTerms: input.validation.matchedTerms,
    practiceAreaIds: input.validation.practiceAreaIds,
    suggestedByUserId: input.userId,
    lastCheckedAt: now,
    lastPullAt: null,
    lastSuccessfulParseAt: null,
    lastStatus: input.validation.accepted ? "ok" : "warning",
    lastStatusMessage: input.validation.summary,
    createdAt: now,
    updatedAt: now,
  };
}

export function isCuratedLegislationFile(file: LegislationFile): file is CuratedLegislationFile {
  return file.id.startsWith("watch-");
}

export function getWatchSource(file: LegislationFile) {
  return isCuratedLegislationFile(file) ? legislativeWatchSources.find((source) => source.id === file.watchJurisdictionId) ?? null : null;
}

function watchFile(
  input: Omit<CuratedLegislationFile, "isCuratedWatch" | "practiceAreaIds" | "status" | "stage" | "deadline" | "ownerFirmId" | "createdByUserId" | "createdAt" | "updatedAt">,
  practiceAreaIds: string[],
  ownerFirmId: string | null,
  createdByUserId: string,
  createdAt: number,
  updatedAt: number,
): CuratedLegislationFile {
  return {
    ...input,
    isCuratedWatch: true,
    practiceAreaIds,
    status: "watched",
    stage: "Source ready",
    deadline: null,
    ownerFirmId,
    createdByUserId,
    createdAt,
    updatedAt,
  };
}

function findPracticeAreaIds(practiceAreas: PracticeArea[], terms: string[]) {
  const normalizedTerms = terms.map((term) => term.toLowerCase());
  return practiceAreas
    .filter((area) => normalizedTerms.some((term) => `${area.name} ${area.description}`.toLowerCase().includes(term)))
    .map((area) => area.id)
    .slice(0, 4);
}

function normalizeSourceUrl(url: string) {
  try {
    const parsed = new URL(url.trim());
    parsed.hash = "";
    return parsed.toString();
  } catch {
    return url.trim();
  }
}

function sourceLabelFromUrl(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "Suggested source";
  }
}

function matchTerms(text: string, terms: readonly string[]) {
  return terms.filter((term) => text.includes(term.toLowerCase()));
}

function termsForPracticeArea(area: PracticeArea) {
  const source = `${area.name} ${area.slug} ${area.description}`.toLowerCase();
  const terms = new Set<string>();
  for (const topic of legislativeWatchTopics) {
    if (source.includes(topic.toLowerCase())) terms.add(topic.toLowerCase());
  }
  for (const [group, keywords] of Object.entries(legislativeSourceKeywordGroups)) {
    if (source.includes(group.toLowerCase()) || keywords.some((keyword) => source.includes(keyword.toLowerCase()))) {
      terms.add(group.toLowerCase());
    }
  }
  return [...terms];
}

function newsItem(
  input: Omit<LegislativeNewsItem, "updatedAt"> & { offsetDays: number },
  now: number,
): LegislativeNewsItem {
  return {
    ...input,
    updatedAt: now - input.offsetDays * day,
  };
}

function linkedWatchFileIdForJurisdiction(jurisdiction?: string | null) {
  const normalized = (jurisdiction ?? "").toLowerCase();
  if (normalized.includes("luxembourg")) return "watch-lu-legilux";
  if (normalized.includes("france")) return "watch-fr-legifrance";
  if (normalized.includes("belgium")) return "watch-be-moniteur";
  if (normalized.includes("switzerland") || normalized.includes("suisse")) return "watch-ch-fedlex";
  return "watch-eu-digital-ai";
}

const hour = 60 * 60 * 1000;
const day = 24 * hour;
